import { withPersonalCourseMaterials } from './course-materials/personal-course-materials';
import { withOrderedCollectionMaterials } from './course-materials/ordered-collection-materials';
// EXPORTS: ICourse, ICourseDay, MOCK_COURSES, CourseSystem

import { Sparkles, Layers, Target, Home, Flame, Zap, Activity } from 'lucide-react';

export type CourseSystem =
  | 'personal'      // 私教跟练合集
  | 'three-way'     // 三分化合集
  | 'four-way'      // 四分化合集
  | 'ksw-collection'// 凯圣王合集
  | 'home'          // 居家训练合集
  | 'fanjue'        // 晋升突破（卧推/深蹲自测，锁定）
  | 'fanjue-plan';  // 焚诀计划（16期进阶训练）

export interface ICourseDay {
  id: string;
  name: string;
  dayIndex: number;
  focus: string;
  durationMin: number;
  warmupIds: string[];
  mainIds: string[];
  stretchIds: string[];
}

export interface ICourse {
  id: string;
  name: string;
  system: CourseSystem;
  description: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  daysPerWeek: number;
  totalDays: number;
  premium?: boolean;
  locked?: boolean;
  days: ICourseDay[];
}

const ORIGINAL_COURSES: ICourse[] = [
  // ========== 一、私教跟练合集（8期·56个动作） ==========
  {
    id: 'personal-stretch',
    name: '第1期·全身完整拉伸',
    system: 'personal',
    description: '15个全身拉伸动作，从头到脚完整放松，保持30-60秒不上重量',
    level: 'beginner',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'personal-stretch-day1',
        name: '全身完整拉伸',
        dayIndex: 1,
        focus: '全身拉伸',
        durationMin: 25,
        warmupIds: [],
        mainIds: [
          'personal-s1-chest-stretch', 'personal-s1-posterior-delt-stretch', 'personal-s1-anterior-delt-stretch',
          'personal-s1-back-tricep-stretch', 'personal-s1-rhomboid-stretch', 'personal-s1-bicep-stretch',
          'personal-s1-abs-oblique-stretch', 'personal-s1-glute-stretch', 'personal-s1-hamstring-stretch',
          'personal-s1-quad-stretch', 'personal-s1-calf-stretch', 'personal-s1-levator-scap-stretch',
          'personal-s1-sternocleidomastoid', 'personal-s1-scalene-release', 'personal-s1-platysma-stretch',
        ],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'personal-back',
    name: '第2期·背部私教跟练',
    system: 'personal',
    description: '9个动作从热身到正式组完整跟练，背部加厚加宽全面覆盖',
    level: 'beginner',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'personal-back-day1',
        name: '背部训练',
        dayIndex: 1,
        focus: '背',
        durationMin: 55,
        warmupIds: ['personal-s2-prep', 'personal-s2-ab-wheel-foam', 'kneel-tricep-dip'],
        mainIds: [
          'fanjue-straight-arm-pulldown', 'fanjue-seated-cable-row',
          'threeway-neutral-pulldown', 'fanjue-seated-cable-row',
          'personal-s2-cable-tricep-pushdown', 'personal-s2-cable-reverse-fly',
        ],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'personal-arms-1',
    name: '第3期·手臂教学·一期',
    system: 'personal',
    description: '6个手臂动作，三头二头交替刺激，1热身+3正式组完整跟练',
    level: 'beginner',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'personal-arm1-day1',
        name: '手臂训练',
        dayIndex: 1,
        focus: '手臂',
        durationMin: 45,
        warmupIds: [],
        mainIds: [
          'fanjue-single-arm-cable-tricep', 'threeway-barbell-skull-crusher',
          'seated-cable-curl', 'personal-s3-supine-dumbbell-curl',
          'personal-s3-overhead-tricep', 'personal-s3-internal-rotation-curl',
        ],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'personal-chest',
    name: '第4期·胸部教学',
    system: 'personal',
    description: '6个胸训动作，热身+平板+上斜+夹胸全面打造胸肌厚度与线条',
    level: 'beginner',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'personal-chest-day1',
        name: '胸部训练',
        dayIndex: 1,
        focus: '胸',
        durationMin: 50,
        warmupIds: ['personal-s2-prep'],
        mainIds: [
          'threeway-dips', 'personal-s4-seated-flat-press', 'fanjue-incline-machine-chest-press',
          'personal-s4-cable-upper-chest', 'personal-s4-shoulder-work',
        ],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'personal-shoulders',
    name: '第5期·肩部教学',
    system: 'personal',
    description: '7个肩部动作，热身+激活+前中后束全方位立体雕刻',
    level: 'beginner',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'personal-shoulder-day1',
        name: '肩部训练',
        dayIndex: 1,
        focus: '肩',
        durationMin: 50,
        warmupIds: ['threeway-shoulder-warmup', 'personal-s5-trx-activation'],
        mainIds: [
          'personal-s5-bent-over-rear-delt', 'threeway-seated-reverse-fly',
          'personal-s5-dumbbell-shoulder-press', 'personal-s5-lateral-raise', 'fanjue-dumbbell-front-raise',
        ],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'personal-legs',
    name: '第6期·腿部训练',
    system: 'personal',
    description: '7个腿训动作，从小腿到大腿前侧后侧全面发展',
    level: 'beginner',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'personal-leg-day1',
        name: '腿部训练',
        dayIndex: 1,
        focus: '腿',
        durationMin: 55,
        warmupIds: ['personal-s2-prep'],
        mainIds: [
          'standing-calf-raise', 'personal-s6-adductor', 'threeway-bulgarian-squat',
          'personal-s6-dumbbell-rdl', 'personal-s6-hip-flexor-raise', 'threeway-lying-leg-curl',
        ],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'personal-abs',
    name: '第7期·腹肌教学',
    system: 'personal',
    description: '3个腹肌核心动作，下腹+上腹+核心轮动完整轰炸',
    level: 'beginner',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'personal-ab-day1',
        name: '腹部训练',
        dayIndex: 1,
        focus: '腹',
        durationMin: 20,
        warmupIds: [],
        mainIds: ['personal-s7-hanging-leg-raise', 'personal-s7-crunch', 'personal-s7-ab-wheel'],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'personal-arms-2',
    name: '第8期·手臂训练·二（超级组）',
    system: 'personal',
    description: '3个超级组动作，三头二头交替轰炸，手臂细节精雕',
    level: 'intermediate',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'personal-arm2-day1',
        name: '手臂超级组',
        dayIndex: 1,
        focus: '手臂超级组',
        durationMin: 35,
        warmupIds: [],
        mainIds: [
          'personal-s8-cable-tricep-pushdown', 'seated-cable-curl',
          'personal-s8-barbell-supine-standing',
        ],
        stretchIds: [],
      },
    ],
  },

  // ========== 二、三分化训练合集（3天）==========
  {
    id: 'three-way-collection',
    name: '三分化·推/拉/蹲 训练合集',
    system: 'three-way',
    description: '经典推拉腿三分化，每周3练，推/拉/蹲各一天，经典增肌塑形训练计划',
    level: 'intermediate',
    daysPerWeek: 3,
    totalDays: 3,
    days: [
      {
        id: 'three-way-day1-push',
        name: 'Day 1 推日',
        dayIndex: 1,
        focus: '胸+肩+手臂',
        durationMin: 60,
        warmupIds: ['threeway-shoulder-warmup'],
        mainIds: ['cable-crossover', 'barbell-flat-press', 'fanjue-incline-machine-chest-press', 'threeway-dips', 'threeway-decline-cable-fly', 'personal-s5-lateral-raise', 'home-dumbbell-curl', 'behind-neck-cable-tricep'],
        stretchIds: [],
      },
      {
        id: 'three-way-day2-pull',
        name: 'Day 2 拉日',
        dayIndex: 2,
        focus: '背+后束+三头',
        durationMin: 60,
        warmupIds: ['personal-s2-prep'],
        mainIds: ['threeway-neutral-pulldown', 'fanjue-lat-pulldown', 'fanjue-seated-cable-row', 'fanjue-seated-cable-row', 'threeway-seated-reverse-fly', 'threeway-seated-reverse-fly', 'fanjue-single-arm-cable-tricep', 'threeway-barbell-skull-crusher'],
        stretchIds: [],
      },
      {
        id: 'three-way-day3-legs',
        name: 'Day 3 蹲日',
        dayIndex: 3,
        focus: '肩+腿+背辅助',
        durationMin: 60,
        warmupIds: ['band-shoulder-warmup', 'threeway-shoulder-warmup'],
        mainIds: ['threeway-smith-press', 'fanjue-seated-cable-row', 'personal-s5-lateral-raise', 'threeway-lying-leg-curl', 'threeway-rhino-squat', 'threeway-bulgarian-squat'],
        stretchIds: [],
      },
    ],
  },

  // ========== 三、四分化训练合集（4天） ==========
  {
    id: 'four-way-collection',
    name: '四分化训练合集',
    system: 'four-way',
    description: '4天4部位，动作标准易上手，适合零基础入门增肌塑形',
    level: 'beginner',
    daysPerWeek: 4,
    totalDays: 4,
    days: [
      {
        id: 'four-day-1',
        name: 'Day 1 推：胸/肩/三头',
        dayIndex: 1,
        focus: '胸+肩+三头',
        durationMin: 65,
        warmupIds: ['ksw-warmup-serratus-activation', 'band-shoulder-warmup'],
        mainIds: ['barbell-flat-press', 'fanjue-incline-machine-chest-press', 'cable-crossover', 'behind-neck-cable-tricep', 'fanjue-standing-military-press'],
        stretchIds: [],
      },
      {
        id: 'four-day-2',
        name: 'Day 2 拉：背/二头',
        dayIndex: 2,
        focus: '背+二头',
        durationMin: 65,
        warmupIds: ['kneel-tricep-dip', 'band-shoulder-warmup'],
        mainIds: ['fanjue-straight-arm-pulldown', 'fanjue-seated-cable-row', 'fanjue-lat-pulldown', 'fanjue-seated-cable-row', 'ksw-cable-curl'],
        stretchIds: [],
      },
      {
        id: 'four-day-3',
        name: 'Day 3 腿',
        dayIndex: 3,
        focus: '腿部全肌群',
        durationMin: 70,
        warmupIds: ['home-worlds-greatest-stretch'],
        mainIds: ['hip-adductor-machine', 'side-lying-hip-adduct', 'single-leg-dumbbell-dl', 'threeway-bulgarian-squat', 'threeway-bulgarian-squat', 'standing-calf-raise'],
        stretchIds: [],
      },
      {
        id: 'four-day-4',
        name: 'Day 4 肩+手臂',
        dayIndex: 4,
        focus: '肩+臂',
        durationMin: 70,
        warmupIds: ['single-leg-dumbbell-dl', 'band-shoulder-warmup', 'push-up'],
        mainIds: ['threeway-seated-reverse-fly', 'threeway-seated-reverse-fly', 'fanjue-standing-military-press', 'cable-face-pull', 'fanjue-single-arm-cable-tricep', 'fanjue-dumbbell-front-raise', 'behind-neck-cable-tricep', 'seated-cable-curl'],
        stretchIds: [],
      },
    ],
  },

  // ========== 凯圣王合集（3期） ==========
  {
    id: 'home-push',
    name: '居家·上肢推',
    system: 'home',
    description: '胸/肩/三头推类日，一对哑铃在家练出上半身推力',
    level: 'beginner',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'home-push-day1',
        name: '上肢推：胸/肩/三头',
        dayIndex: 1,
        focus: '胸+肩+三头',
        durationMin: 60,
        warmupIds: ['band-shoulder-warmup', 'threeway-seated-reverse-fly'],
        mainIds: ['home-wall-handstand', 'home-dumbbell-pushup', 'barbell-flat-press', 'cable-crossover', 'home-seated-behind-neck-tricep', 'fanjue-single-arm-cable-tricep'],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'home-pull',
    name: '居家·上肢拉',
    system: 'home',
    description: '背/二头/核心拉类日，哑铃+自重打造背部线条和手臂',
    level: 'beginner',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'home-pull-day1',
        name: '上肢拉：背/二头/核心',
        dayIndex: 1,
        focus: '背+二头+核心',
        durationMin: 60,
        warmupIds: ['home-prone-back-extension'],
        mainIds: ['personal-s7-ab-wheel', 'fanjue-seated-cable-row', 'threeway-neutral-pulldown', 'fanjue-seated-cable-row', 'home-dumbbell-curl'],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'home-lower-body',
    name: '居家·下肢',
    system: 'home',
    description: '腿/臀/核心下肢日，哑铃深蹲硬拉臀桥，居家打造臀腿',
    level: 'beginner',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'home-lower-day1',
        name: '下肢：腿/臀/核心',
        dayIndex: 1,
        focus: '腿+臀+核心',
        durationMin: 65,
        warmupIds: ['home-worlds-greatest-stretch'],
        mainIds: ['home-side-plank-v2', 'threeway-bulgarian-squat', 'threeway-bulgarian-squat', 'home-goblet-squat', 'ksw-barbell-rdl', 'standing-calf-raise'],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'home-shoulder-arm',
    name: '居家·肩臂',
    system: 'home',
    description: '肩/臂混合日，前中后束+二头三头全角度雕刻',
    level: 'beginner',
    daysPerWeek: 1,
    totalDays: 1,
    days: [
      {
        id: 'home-shoulder-arm-day1',
        name: '肩臂：肩+二头+三头',
        dayIndex: 1,
        focus: '肩+臂',
        durationMin: 60,
        warmupIds: ['band-shoulder-warmup', 'threeway-seated-reverse-fly'],
        mainIds: ['threeway-seated-reverse-fly', 'threeway-smith-press', 'fanjue-dumbbell-front-raise', 'personal-s5-lateral-raise', 'home-dumbbell-curl', 'home-seated-behind-neck-tricep'],
        stretchIds: [],
      },
    ],
  },

  // ========== 凯圣王合集（3期） ==========
  {
    id: 'ksw-collection-push',
    name: '第1期·胸肩三头',
    system: 'ksw-collection',
    description: '凯圣王第1期：杠铃卧推主打，上斜哑铃卧推+双杠臂屈伸补上下胸，Y字侧平举收尾',
    level: 'intermediate',
    daysPerWeek: 1,
    totalDays: 1,
    locked: true,
    days: [
      {
        id: 'ksw-day-1',
        name: '推日·胸/肩/三头',
        dayIndex: 1,
        focus: '胸+肩+三头',
        durationMin: 60,
        warmupIds: [
          'ksw-warmup-pec-minor-release',
          'ksw-warmup-pec-major-release',
          'ksw-warmup-biceps-release',
          'band-shoulder-warmup',
          'ksw-warmup-lower-trap-y',
          'ksw-warmup-serratus-activation',
          'ksw-warmup-wrist-activation',
        ],
        mainIds: [
          'barbell-flat-press',
          'fanjue-incline-machine-chest-press',
          'threeway-dips',
          'ksw-lying-extension',
          'ksw-y-raise',
        ],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'ksw-collection-pull',
    name: '第2期·背+后束+二头',
    system: 'ksw-collection',
    description: '凯圣王第2期：单手钢线下拉+器械划船+对握下拉+开肘拉+钢线弯举，背臂全面轰炸',
    level: 'intermediate',
    daysPerWeek: 1,
    totalDays: 1,
    locked: true,
    days: [
      {
        id: 'ksw-day-2',
        name: '拉日·背/后束/二头',
        dayIndex: 2,
        focus: '背+后束+二头',
        durationMin: 60,
        warmupIds: [
          'home-worlds-greatest-stretch',
          'band-shoulder-warmup',
          'fanjue-stretch-counter',
          'ksw-warmup-thoracic-flexion',
          'ksw-warmup-thoracic-proprioception',
        ],
        mainIds: [
          'ksw-single-arm-cable-pulldown',
          'fanjue-seated-cable-row',
          'threeway-neutral-pulldown',
          'threeway-neutral-pulldown',
          'ksw-cable-curl',
        ],
        stretchIds: [],
      },
    ],
  },
  {
    id: 'ksw-collection-legs',
    name: '第3期·腿',
    system: 'ksw-collection',
    description: '凯圣王第3期：单腿硬拉+保加利亚蹲+高脚杯深蹲+罗马尼亚硬拉+山羊挺身，下肢全方位强化',
    level: 'intermediate',
    daysPerWeek: 1,
    totalDays: 1,
    locked: true,
    days: [
      {
        id: 'ksw-day-3',
        name: '腿日·下肢全肌群',
        dayIndex: 3,
        focus: '腿+臀+下背',
        durationMin: 60,
        warmupIds: [
          'ksw-warmup-dynamic-frog',
          'ksw-warmup-hip-90-90',
          'ksw-warmup-ankle-activation',
          'ksw-warmup-foam-calf',
          'ksw-warmup-foam-quad',
          'ksw-warmup-foam-glute',
          'ksw-warmup-foam-tfl',
          'ksw-warmup-foam-tibialis',
          'home-worlds-greatest-stretch',
        ],
        mainIds: [
          'single-leg-dumbbell-dl',
          'threeway-bulgarian-squat',
          'home-goblet-squat',
          'ksw-barbell-rdl',
          'ksw-back-extension',
        ],
        stretchIds: [],
      },
    ],
  },

  // ========== 晋升突破（卧推/深蹲自测，会员专属）==========
  {
    id: 'promotion-breakthrough',
    name: '晋升突破·卧推深蹲自测',
    system: 'fanjue',
    description: '进阶力量自测评估，通过卧推与深蹲1RM测试生成专属进阶训练计划，仅限会员解锁',
    level: 'advanced',
    daysPerWeek: 2,
    totalDays: 0,
    locked: true,
    premium: true,
    days: [],
  },

  // ========== 焚诀计划（16期课程）==========
  {
    id: 'fanjue-plan',
    name: '焚诀计划·16期进阶力量训练',
    system: 'fanjue-plan',
    description: '16期系统进阶力量训练，胸背腿肩臂核心全覆盖，动作精讲+完整跟练',
    level: 'advanced',
    daysPerWeek: 4,
    totalDays: 16,
    locked: true,
    days: [
      {
        id: 'fanjue-day1',
        name: '第01期 · 背+肩热身',
        dayIndex: 1,
        focus: '背+三头',
        durationMin: 60,
        warmupIds: ['band-shoulder-warmup', 'personal-s1-chest-stretch'],
        mainIds: ['fanjue-lat-pulldown', 'fanjue-straight-arm-pulldown'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day2',
        name: '第02期 · 背',
        dayIndex: 2,
        focus: '背部厚度+宽度',
        durationMin: 60,
        warmupIds: [],
        mainIds: ['fanjue-seated-cable-row', 'fanjue-straight-arm-pulldown', 'fanjue-seated-cable-row', 'fanjue-lat-pulldown'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day3',
        name: '第03期 · 核心（健腹轮·待补充）',
        dayIndex: 3,
        focus: '核心训练',
        durationMin: 60,
        warmupIds: [],
        mainIds: [],
        stretchIds: [],
      },
      {
        id: 'fanjue-day4',
        name: '第04期 · 背',
        dayIndex: 4,
        focus: '背部划船',
        durationMin: 60,
        warmupIds: ['personal-s2-prep'],
        mainIds: ['fanjue-seated-cable-row', 'personal-s2-prep'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day5',
        name: '第05期 · 腿',
        dayIndex: 5,
        focus: '下肢单侧训练',
        durationMin: 60,
        warmupIds: ['ksw-warmup-dynamic-frog'],
        mainIds: ['threeway-bulgarian-squat'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day6',
        name: '第06期 · 腿屈伸',
        dayIndex: 6,
        focus: '股四头肌精确刺激',
        durationMin: 60,
        warmupIds: [],
        mainIds: ['fanjue-seated-leg-extension', 'fanjue-seated-leg-extension', 'fanjue-seated-leg-extension'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day7',
        name: '第07期 · 肩后束',
        dayIndex: 7,
        focus: '三角肌后束',
        durationMin: 60,
        warmupIds: ['personal-s2-prep', 'fanjue-stretch-counter', 'personal-s2-prep'],
        mainIds: ['threeway-seated-reverse-fly', 'threeway-shoulder-warmup'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day8',
        name: '第08期 · 背',
        dayIndex: 8,
        focus: '背阔+三头',
        durationMin: 60,
        warmupIds: ['threeway-shoulder-warmup'],
        mainIds: ['threeway-neutral-pulldown', 'fanjue-single-arm-cable-tricep'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day9',
        name: '第09期 · 胸',
        dayIndex: 9,
        focus: '胸大肌',
        durationMin: 60,
        warmupIds: ['personal-s2-prep'],
        mainIds: ['fanjue-incline-machine-chest-press', 'cable-crossover'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day10',
        name: '第10期 · 肩',
        dayIndex: 10,
        focus: '肩部推举',
        durationMin: 60,
        warmupIds: ['threeway-shoulder-warmup'],
        mainIds: ['threeway-shoulder-warmup', 'fanjue-standing-military-press'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day11',
        name: '第11期 · 胸',
        dayIndex: 11,
        focus: '胸部全面轰炸',
        durationMin: 60,
        warmupIds: ['personal-s2-prep'],
        mainIds: ['fanjue-incline-smith-press', 'fanjue-incline-machine-chest-press', 'personal-s5-lateral-raise', 'threeway-dips', 'cable-crossover', 'cable-crossover'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day12',
        name: '第12期 · 肩',
        dayIndex: 12,
        focus: '肩部全角度',
        durationMin: 60,
        warmupIds: ['threeway-shoulder-warmup'],
        mainIds: ['personal-s5-lateral-raise', 'threeway-smith-press', 'personal-s5-lateral-raise', 'fanjue-dumbbell-front-raise', 'cable-face-pull'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day13',
        name: '第13期 · 核心',
        dayIndex: 13,
        focus: '核心整体',
        durationMin: 60,
        warmupIds: [],
        mainIds: ['personal-s7-hanging-leg-raise', 'personal-s7-hanging-leg-raise', 'personal-s7-crunch'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day14',
        name: '第14期 · 手臂',
        dayIndex: 14,
        focus: '二头+三头',
        durationMin: 60,
        warmupIds: [],
        mainIds: ['threeway-barbell-skull-crusher', 'ksw-cable-curl', 'fanjue-single-arm-cable-tricep', 'fanjue-single-arm-cable-tricep', 'ksw-cable-curl', 'ksw-cable-curl'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day15',
        name: '第15期 · 臀',
        dayIndex: 15,
        focus: '臀部+核心',
        durationMin: 60,
        warmupIds: ['fanjue-copenhagen-plank', 'personal-s2-prep', 'ksw-warmup-dynamic-frog'],
        mainIds: ['band-shoulder-warmup', 'threeway-bulgarian-squat', 'fanjue-wide-body-function', 'personal-s7-hanging-leg-raise'],
        stretchIds: [],
      },
      {
        id: 'fanjue-day16',
        name: '第16期 · 腿',
        dayIndex: 16,
        focus: '腿部全面',
        durationMin: 60,
        warmupIds: ['personal-s2-prep'],
        mainIds: ['threeway-bulgarian-squat', 'threeway-lying-leg-curl', 'fanjue-seated-leg-extension', 'threeway-rhino-squat'],
        stretchIds: [],
      },
    ],
  },
];

export const MOCK_COURSES: ICourse[] = ORIGINAL_COURSES.map(withPersonalCourseMaterials).map(withOrderedCollectionMaterials);

export function getCourseById(id: string): ICourse | undefined {
  return MOCK_COURSES.find(c => c.id === id);
}

export function getCoursesBySystem(system: CourseSystem): ICourse[] {
  return MOCK_COURSES.filter(c => c.system === system);
}

export interface ICollectionMeta {
  system: CourseSystem;
  title: string;
  subtitle: string;
  description: string;
  icon: typeof Sparkles;
  accentFrom: string;
  accentTo: string;
  unitLabel: string; // "期" 或 "天"
  locked?: boolean;
}

export const COLLECTION_META: ICollectionMeta[] = [
  {
    system: 'home',
    title: '居家训练合集',
    subtitle: '居家无器械',
    description: '居家自重+弹力带训练，全身/上肢/下肢/核心全覆盖，无需器械',
    icon: Home,
    accentFrom: 'from-emerald-400',
    accentTo: 'to-teal-500',
    unitLabel: '套',
  },
  {
    system: 'personal',
    title: '私教跟练合集',
    subtitle: '8期完整跟练',
    description: '私教级跟练课程，8期56个动作从头到脚精细打磨，每期完整热身+正式组跟练',
    icon: Sparkles,
    accentFrom: 'from-amber-400',
    accentTo: 'to-orange-500',
    unitLabel: '期',
  },
  {
    system: 'three-way',
    title: '三分化训练合集',
    subtitle: '3天推拉蹲',
    description: '经典推/拉/蹲三分化训练，每周3练，增肌减脂兼顾',
    icon: Layers,
    accentFrom: 'from-blue-400',
    accentTo: 'to-indigo-500',
    unitLabel: '天',
  },
  {
    system: 'four-way',
    title: '四分化训练合集',
    subtitle: '4天精细分化',
    description: '四分化训练合集，胸/背/腿/肩臂独立训练日，动作标准易上手',
    icon: Target,
    accentFrom: 'from-purple-400',
    accentTo: 'to-violet-500',
    unitLabel: '天',
  },
  {
    system: 'ksw-collection',
    title: '凯圣王合集',
    subtitle: '3期完整课程',
    description: '凯圣王原创三分化跟练合集，推/拉/腿3期完整课程，附详细热身和动作要点',
    icon: Zap,
    accentFrom: 'from-rose-500',
    accentTo: 'to-orange-500',
    unitLabel: '期',
    locked: true,
  },
  {
    system: 'fanjue-plan',
    title: '焚诀计划',
    subtitle: '16期进阶训练',
    description: '16期系统进阶力量训练课程，胸背腿肩臂核心全覆盖，动作精讲+完整跟练',
    icon: Flame,
    accentFrom: 'from-amber-500',
    accentTo: 'to-red-600',
    unitLabel: '期',
    locked: true,
  },
  {
    system: 'fanjue',
    title: '晋升突破',
    subtitle: '卧推·深蹲自测',
    description: '进阶力量自测评估，卧推与深蹲1RM测试，生成专属进阶训练计划',
    icon: Activity,
    accentFrom: 'from-slate-700',
    accentTo: 'to-slate-900',
    unitLabel: '项',
    locked: true,
  },
];

export function getCollectionMeta(system: CourseSystem): ICollectionMeta | undefined {
  return COLLECTION_META.find(c => c.system === system);
}

export interface IDayWithCourse {
  day: ICourseDay;
  course: ICourse;
}

export function getDayById(dayId: string): IDayWithCourse | undefined {
  for (const course of MOCK_COURSES) {
    const day = course.days.find(d => d.id === dayId);
    if (day) return { day, course };
  }
  return undefined;
}
